/** @jsxImportSource @emotion/react */

// Lienzo de un flujograma de pasos. Lo usan la configuración del flujo de un
// tipo de actividad y la vista de una actividad: cada una decide qué va dentro
// de la caja de cada paso; la ubicación de las cajas y las flechas es la misma.
//
// Los pasos se ubican por niveles: el nivel de un paso es a cuántas
// aprobaciones está del paso inicial, así que los pasos que se abren en
// paralelo quedan lado a lado y el paso donde se unen, debajo. Los dos finales
// ("Fin" y "Fin sin cumplimiento") van lado a lado, al final.
//
// Aprobar se dibuja en verde y rechazar en rojo punteado: lo que va al nivel
// de abajo sale recto; los saltos y las vueltas atrás van en arcos por los
// costados (aprobar por la derecha, rechazar por la izquierda), y "se queda"
// es un lazo debajo del paso.

const SEPARACION = 36;
const SEPARACION_VERTICAL = 56;
const MARGEN_LATERAL = 95;
const MARGEN_SUPERIOR = 12;
const ANCHO_FIN = 76;
const ANCHO_FIN_SIN_CUMPLIMIENTO = 170;
const ALTO_FIN = 34;

const VERDE = "var(--verde)";
const ROJO = "var(--rojo)";

// Los dos finales de la actividad
const FIN = "fin";
const FIN_SIN_CUMPLIMIENTO = "fin_sin_cumplimiento";
const finalDe = (transicion) =>
  transicion.sinCumplimiento ? FIN_SIN_CUMPLIMIENTO : FIN;

// Nivel de cada paso: distancia al paso inicial siguiendo aprobaciones. A los
// que solo se llega rechazando se los ubica siguiendo cualquier transición, y
// a los que no se llega de ninguna forma (los que están sin conectar), al
// final.
function calcularNiveles(pasos, transiciones, inicialId) {
  const nivel = new Map();
  const recorrer = (soloAprobar) => {
    const cola = [...nivel.keys()];
    while (cola.length) {
      const actual = cola.shift();
      for (const t of transiciones) {
        if (t.origenId !== actual || t.destinoId === null) continue;
        if (soloAprobar && t.resultado !== "APROBADO") continue;
        if (nivel.has(t.destinoId)) continue;
        nivel.set(t.destinoId, nivel.get(actual) + 1);
        cola.push(t.destinoId);
      }
    }
  };
  nivel.set(inicialId, 0);
  recorrer(true);
  recorrer(false);
  let ultimo = Math.max(...nivel.values());
  for (const paso of pasos) {
    if (!nivel.has(paso.id)) nivel.set(paso.id, ++ultimo);
  }
  return nivel;
}

/**
 * @param {{id: number}[]} pasos en orden
 * @param {{origenId: number, resultado: "APROBADO"|"RECHAZADO", destinoId: number|null, sinCumplimiento?: boolean}[]} transiciones
 *   destinoId null es un final de la actividad; con sinCumplimiento, el fin sin cumplimiento
 * @param {number|null} pasoInicialId paso en el que empieza la actividad
 * @param {number} anchoPaso ancho de la caja de un paso, en px
 * @param {(paso) => number} altoDePaso alto de la caja de un paso, en px
 * @param {(paso) => object} [estiloDePaso] estilos extra para la caja de un paso
 * @param {(paso) => React.ReactNode} renderPaso contenido de la caja de un paso
 */
export default function Flujograma({
  pasos,
  transiciones,
  pasoInicialId,
  anchoPaso,
  altoDePaso,
  estiloDePaso,
  renderPaso
}) {
  if (!pasos?.length) return null;

  const idsValidos = new Set(pasos.map((p) => p.id));
  const validas = transiciones.filter(
    (t) =>
      idsValidos.has(t.origenId) &&
      (t.destinoId === null || idsValidos.has(t.destinoId))
  );

  // Sin paso inicial elegido se dibuja desde el primero
  const inicialId = idsValidos.has(pasoInicialId) ? pasoInicialId : pasos[0].id;
  const nivel = calcularNiveles(pasos, validas, inicialId);

  // Filas: los pasos de cada nivel, en su orden. Cada fila es tan alta como
  // su paso más alto.
  const filas = [];
  for (const paso of pasos) {
    (filas[nivel.get(paso.id)] ||= []).push(paso);
  }
  const filasConPasos = filas.filter(Boolean);
  const anchoFila = (cantidad) =>
    cantidad * anchoPaso + (cantidad - 1) * SEPARACION;
  const anchoFinales = ANCHO_FIN + SEPARACION + ANCHO_FIN_SIN_CUMPLIMIENTO;
  const ancho =
    Math.max(
      ...filasConPasos.map((fila) => anchoFila(fila.length)),
      anchoFinales
    ) +
    MARGEN_LATERAL * 2;

  // Posición de cada nodo (centro, y mitades de ancho y alto)
  const nodos = new Map();
  let arriba = MARGEN_SUPERIOR;
  filasConPasos.forEach((fila, n) => {
    const altos = fila.map((paso) => altoDePaso(paso));
    const altoFila = Math.max(...altos);
    const inicio = (ancho - anchoFila(fila.length)) / 2;
    fila.forEach((paso, i) => {
      nodos.set(paso.id, {
        nivel: n,
        x: inicio + i * (anchoPaso + SEPARACION) + anchoPaso / 2,
        // Los pasos de una fila se alinean arriba
        y: arriba + altos[i] / 2,
        mitadX: anchoPaso / 2,
        mitadY: altos[i] / 2
      });
    });
    arriba += altoFila + SEPARACION_VERTICAL;
  });

  const nivelFin = filasConPasos.length;
  const yFin = arriba + ALTO_FIN / 2;
  const alto = yFin + ALTO_FIN / 2 + MARGEN_SUPERIOR;
  const inicioFinales = (ancho - anchoFinales) / 2;
  const finales = [
    { clave: FIN, texto: "Fin", ancho: ANCHO_FIN, x: inicioFinales + ANCHO_FIN / 2 },
    {
      clave: FIN_SIN_CUMPLIMIENTO,
      texto: "Fin sin cumplimiento",
      ancho: ANCHO_FIN_SIN_CUMPLIMIENTO,
      x: inicioFinales + ANCHO_FIN + SEPARACION + ANCHO_FIN_SIN_CUMPLIMIENTO / 2
    }
  ];
  for (const final of finales) {
    nodos.set(final.clave, {
      nivel: nivelFin,
      x: final.x,
      y: yFin,
      mitadX: final.ancho / 2,
      mitadY: ALTO_FIN / 2
    });
  }

  const flechas = validas.map((transicion) => {
    const desde = nodos.get(transicion.origenId);
    const hasta = nodos.get(transicion.destinoId ?? finalDe(transicion));
    const aprueba = transicion.resultado === "APROBADO";
    const lado = aprueba ? 1 : -1;
    const clave = `${transicion.origenId}-${transicion.resultado}-${
      transicion.destinoId ?? finalDe(transicion)
    }`;
    let d;

    if (hasta === desde) {
      // Se queda en el mismo paso: un lazo debajo de la caja, hacia su lado,
      // para no pisar al paso que tenga al costado.
      const x = desde.x + lado * (desde.mitadX - 36);
      const base = desde.y + desde.mitadY;
      d = `M ${x - lado * 12} ${base} C ${x - lado * 30} ${base + 34}, ${
        x + lado * 30
      } ${base + 34}, ${x + lado * 11} ${base + 3}`;
    } else if (hasta.nivel === desde.nivel + 1) {
      // Al nivel de abajo: recto. Rechazar va apenas corrido, para que no se
      // pise con aprobar cuando los dos van al mismo paso.
      const corrimiento = aprueba ? 0 : -22;
      d = `M ${desde.x + corrimiento} ${desde.y + desde.mitadY} L ${
        hasta.x + corrimiento
      } ${hasta.y - hasta.mitadY - 2}`;
    } else {
      // Salto o vuelta atrás: arco por el costado, más abierto cuanto más
      // lejos, para que los arcos no se encimen.
      const x1 = desde.x + lado * desde.mitadX;
      const x2 = hasta.x + lado * hasta.mitadX;
      const distancia = Math.abs(hasta.nivel - desde.nivel) || 1;
      const apertura = Math.min(34 + distancia * 16, MARGEN_LATERAL - 6);
      const control =
        (lado > 0 ? Math.max(x1, x2) : Math.min(x1, x2)) + lado * apertura;
      const haciaAbajo = hasta.nivel > desde.nivel;
      const y1 = desde.y + (haciaAbajo ? 12 : -12);
      const y2 = hasta.y + (haciaAbajo ? -12 : 12);
      d = `M ${x1} ${y1} C ${control} ${y1}, ${control} ${y2}, ${
        x2 + lado * 2
      } ${y2}`;
    }
    return { clave, aprueba, d };
  });

  return (
    <div>
      {/* Si el flujo es más ancho que su contenedor, se desplaza hacia los
          costados */}
      <div css={{ overflowX: "auto" }}>
        <div
          css={{
            position: "relative",
            width: `${ancho}px`,
            height: `${alto}px`,
            // Centrado en su contenedor
            margin: "0 auto"
          }}
        >
          <svg
            width={ancho}
            height={alto}
            role="img"
            aria-label="Flujograma de los pasos"
            css={{ position: "absolute", top: 0, left: 0 }}
          >
            <defs>
              {[
                ["flecha-aprueba", VERDE],
                ["flecha-rechaza", ROJO]
              ].map(([id, color]) => (
                <marker
                  key={id}
                  id={id}
                  viewBox="0 0 10 10"
                  refX="9"
                  refY="5"
                  markerWidth="7"
                  markerHeight="7"
                  orient="auto"
                >
                  <path d="M0 0L10 5L0 10z" style={{ fill: color }} />
                </marker>
              ))}
            </defs>
            {flechas.map((flecha) => (
              <path
                key={flecha.clave}
                d={flecha.d}
                fill="none"
                strokeWidth="1.5"
                strokeDasharray={flecha.aprueba ? undefined : "5 4"}
                markerEnd={`url(#${
                  flecha.aprueba ? "flecha-aprueba" : "flecha-rechaza"
                })`}
                style={{ stroke: flecha.aprueba ? VERDE : ROJO }}
              />
            ))}
          </svg>

          {/* Las cajas van encima de las flechas: si un arco pasa por detrás
              de un paso, queda tapado. */}
          {pasos.map((paso) => {
            const nodo = nodos.get(paso.id);
            return (
              <div
                key={paso.id}
                css={{
                  position: "absolute",
                  left: `${nodo.x - nodo.mitadX}px`,
                  top: `${nodo.y - nodo.mitadY}px`,
                  width: `${anchoPaso}px`,
                  height: `${nodo.mitadY * 2}px`,
                  boxSizing: "border-box",
                  backgroundColor: "white",
                  border: "1px solid var(--grisTextos)",
                  borderRadius: "8px",
                  fontSize: "0.8rem",
                  lineHeight: 1.35,
                  ...(estiloDePaso ? estiloDePaso(paso) : {})
                }}
              >
                {renderPaso(paso)}
              </div>
            );
          })}

          {finales.map((final) => (
            <div
              key={final.clave}
              css={{
                position: "absolute",
                left: `${final.x - final.ancho / 2}px`,
                top: `${yFin - ALTO_FIN / 2}px`,
                width: `${final.ancho}px`,
                height: `${ALTO_FIN}px`,
                boxSizing: "border-box",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                backgroundColor: "var(--surface-200)",
                border: "1px solid var(--grisTextos)",
                borderRadius: `${ALTO_FIN / 2}px`,
                fontSize: "0.8rem"
              }}
            >
              {final.texto}
            </div>
          ))}
        </div>
      </div>

      <div
        css={{
          display: "flex",
          justifyContent: "center",
          gap: "1.5em",
          fontSize: "0.8rem",
          color: "var(--grisTextos)",
          marginTop: "0.5em"
        }}
      >
        <span>
          <span css={{ color: VERDE, fontWeight: 700 }}>——</span> Aprueba
        </span>
        <span>
          <span css={{ color: ROJO, fontWeight: 700 }}>- - -</span> Rechaza
        </span>
      </div>
    </div>
  );
}
